import { PaymentElement, useElements, useStripe } from '@stripe/react-stripe-js';
import { useEffect, useState } from 'react';
import { DispatchSnackbarService } from '../../../services';
import { useDispatch } from '../../../store';
import { config } from '../../../env';
import Loader from '../../../components/Loader';
import { getStripeErrorMessage } from '../../../services/snackbar/dispatch-snackbar.service';
import { trackPurchase } from '../../../utils/gtm';
import { StripePaymentElementChangeEvent } from '@stripe/stripe-js';

type Props = {
  handleEventChange: (event: StripePaymentElementChangeEvent) => void;
  submit: boolean;
  loadCompleted: () => void;
  handleConfirmLoading: (loading: boolean) => void;
  saveCard?: boolean;
  saveCardResponse?: (saved: any) => void;
  handleSuccess?: (success: boolean) => void;
};

const service = new DispatchSnackbarService();

const StripeCheckout = ({
  handleEventChange,
  submit,
  loadCompleted,
  handleConfirmLoading,
  saveCard,
  saveCardResponse,
  handleSuccess
}: Props) => {
  const stripe = useStripe();
  const elements = useElements();
  const [confirmLoading, setConfirmLoading] = useState(false);
  const dispatch = useDispatch();
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    handleConfirmLoading(confirmLoading);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [confirmLoading]);

  useEffect(() => {
    if (submit) {
      handleSubmit();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [submit]);

  useEffect(() => {
    if (saveCard) {
      handleCardSave();
    }
  }, [saveCard]);

  const handleCardSave = () => {
    if (!stripe || !elements) {
      // Stripe.js has not yet loaded.
      // Make sure to disable form submission until Stripe.js has loaded.
      return;
    }
    setLoading(true);
    setConfirmLoading(true);
    stripe
      ?.confirmSetup({
        elements,
        confirmParams: {
          // Return URL where the customer should be redirected after the SetupIntent is confirmed.
          return_url: `${config.hostname}/account-settings/billing`
        },
        redirect: 'if_required'
      })
      .then(function (result) {
        setLoading(false);
        setConfirmLoading(false);
        console.log('res: ', result);
        if (result.error) {
          // Inform the customer that there was an error.
          service.dispatchSnackbar(dispatch, getStripeErrorMessage(result?.error?.code), 'alert', 'error', 5000);
          console.log('ERROR: ', result.error);
        } else {
          service.dispatchSnackbar(dispatch, 'Card added successfully', 'alert', 'success', 5000);
          if (saveCardResponse) {
            saveCardResponse(result.setupIntent);
          }
        }
      });
  };

  const handleSubmit = () => {
    if (!stripe || !elements) {
      // Stripe.js has not yet loaded.
      // Make sure to disable form submission until Stripe.js has loaded.
      return;
    }
    setConfirmLoading(true);

    stripe
      .confirmPayment({
        //`Elements` instance that was used to create the Payment Element
        elements,
        confirmParams: {
          return_url: `${config.hostname}/account-settings/billing`
        },
        redirect: 'if_required'
      })
      .then((result) => {
        console.log(result);
        if (result?.error) {
          console.log(result.error.message);
          service.dispatchSnackbar(dispatch, getStripeErrorMessage(result?.error?.code), 'alert', 'error', 5000);
          setConfirmLoading(false);
        } else {
          trackPurchase({
            transaction_id: result?.paymentIntent?.id || '',
            value: (result?.paymentIntent?.amount ?? 0) / 100,
            currency: result?.paymentIntent?.currency || 'usd',
            checkout_entry: 'subscription_checkout'
          });
          service.dispatchSnackbar(dispatch, 'Payment successful. Please wait upgrading your subscription', 'alert', 'success', 5000);
          setConfirmLoading(false);
          if (handleSuccess) {
            setLoading(true);
            handleSuccess(true);
          }
        }
      })
      .catch((err) => {
        console.log(err.message);
        setConfirmLoading(false);
        const msz = err.message === 'no permission to access this RPC' ? 'Permission denied' : err.message;
        service.dispatchSnackbar(dispatch, msz, 'alert', 'error', 5000);
      });
  };

  return (
    <>
      {loading && <Loader />}
      <form id="stripe-card">
        <PaymentElement onReady={loadCompleted} onChange={handleEventChange} />
      </form>
    </>
  );
};

export default StripeCheckout;
